/**
 * hostRegistry.js — single source of truth for hostname → mode/route.
 *
 * Extracted from RootRouter.jsx in Sprint 3.7.9u to (a) stop the same
 * host set being redefined in multiple places, (b) make it obvious
 * why an app.* subdomain renders one page vs another, and (c) unblock
 * future one-liner additions when a new module domain is spun up.
 *
 * Two exports:
 *   • `HOST_MODE_MAP`  — { hostname: modeString }  Direct string modes
 *     consumed by RootRouter's mode dispatch. Includes app.* subdomain
 *     modes ("answer-app", "answer-demo") that render inline components
 *     without a Navigate round-trip.
 *   • `APP_HOST_ROUTES` — { hostname: pathString }  Bare paths for
 *     hosts that should Navigate() into the SPA (login, dashboards,
 *     etc.). RootRouter still emits <Navigate to={path} replace/>.
 *
 * Consumers must call the two exported helpers rather than inline
 * hash lookups so we can add cheap host aliases (www., demo. strip)
 * in one place.
 */

// Bare hostnames that map to an in-page RootRouter mode. First hit wins.
// Preserving key insertion order matters when a hostname could otherwise
// match Plaza/module fallbacks (e.g. `app.hustleanswer.au` must resolve
// to `answer-app` before Plaza mode grabs it).
const _MAP = {
  // ── PWA launcher (Sprint 3.7.9ah, Feb 2026): pwa.hustlesuite.au
  //    is a native-app-drawer style grid of every module. Post-login
  //    users on the umbrella host land here.
  "pwa.hustlesuite.au":     "pwa-launcher",
  "www.pwa.hustlesuite.au": "pwa-launcher",

  // ── Vendors / Members portals (Sprint 3.7.9aj, Feb 2026):
  //    vendors.hustlesuite.au   → VendorsLandingPage
  //    members.hustlesuite.au   → MembersLandingPage
  //    Every vendor also has a member account — the AccountPage
  //    exposes a switcher.
  "vendors.hustlesuite.au":     "vendors-landing",
  "www.vendors.hustlesuite.au": "vendors-landing",
  "members.hustlesuite.au":     "members-landing",
  "www.members.hustlesuite.au": "members-landing",

  // ── HustleAnswer standalone (renders components directly) ──────
  "app.hustleanswer.au":     "answer-app",
  "www.app.hustleanswer.au": "answer-app",
  "demo.hustleanswer.au":    "answer-demo",
  "www.demo.hustleanswer.au":"answer-demo",
  "hustleanswer.au":         "answer-sales",
  "www.hustleanswer.au":     "answer-sales",

  // ── Discovery / finder ────────────────────────────────────────
  "hustlefinder.au":     "finder",
  "www.hustlefinder.au": "finder",
  "hustlefind.au":       "finder",
  "www.hustlefind.au":   "finder",

  // ── Bare-domain sales pages ───────────────────────────────────
  "hustletravel.au":     "travel-sales",
  "www.hustletravel.au": "travel-sales",
  "hustledictate.au":     "dictate-sales",
  "www.hustledictate.au": "dictate-sales",
  "app.hustledictate.au": "dictate-sales",
  "hustleevents.au":     "events-sales",
  "www.hustleevents.au": "events-sales",
  "hustlemeet.au":     "meet-landing",
  "www.hustlemeet.au": "meet-landing",
  "hustlecircles.au":     "circles-sales",
  "www.hustlecircles.au": "circles-sales",
  "hustlematchmaker.au":     "matchmaker-sales",
  "www.hustlematchmaker.au": "matchmaker-sales",
  "hustleroundup.au":     "roundup-sales",
  "www.hustleroundup.au": "roundup-sales",
  "hustlerewards.au":     "rewards-sales",
  "www.hustlerewards.au": "rewards-sales",
  "hustledirectory.au":     "directory-sales",
  "www.hustledirectory.au": "directory-sales",
  // iter358 (Feb 2026) — app.hustledirectory.au must render the full
  // directory experience AT THE ROOT (no /directory subpath redirect,
  // no login gate). Serves DirectoryPage inline so the SEO/AEO team
  // can point cross-site links at `https://app.hustledirectory.au/`
  // without a subfolder in the URL.
  "app.hustledirectory.au":     "directory-app",
  "www.app.hustledirectory.au": "directory-app",
  "hustlestadium.au":     "stadium-sales",
  "www.hustlestadium.au": "stadium-sales",
  "hustleeats.au":     "eats-sales",
  "www.hustleeats.au": "eats-sales",
  "hustleconnect.au":         "connect-sales",
  "www.hustleconnect.au":     "connect-sales",
  "gethustleconnect.com":     "connect-sales",
  "www.gethustleconnect.com": "connect-sales",
  "hustlereviews.au":     "reviews-sales",
  "www.hustlereviews.au": "reviews-sales",
  "hustlecrm.au":     "crm-sales",
  "www.hustlecrm.au": "crm-sales",
  "hustlepress.au":     "press-sales",
  "www.hustlepress.au": "press-sales",

  // ── HustleShield Forms — SaaS sales + app ────────────────────
  // hustleshield.au apex renders the AUD sales page at `/` (clean URL,
  // no `/hustleshield.au` subfolder). emailspamshield.com is the USD
  // mirror. The app lives on app.hustleshield.au and is handled by the
  // APP_HOST_ROUTES map below.
  "hustleshield.au":        "shield-sales",
  "www.hustleshield.au":    "shield-sales",
  "emailspamshield.com":    "shield-us-sales",
  "www.emailspamshield.com":"shield-us-sales",

  // ── HustleProfitMaps — local-rank grid (exposure grid) sales page.
  //    Root + www + demo render the sales pitch; app.* jumps into the
  //    product (login → /admin/exposure-grid).
  "hustleprofitmaps.au":      "profitmaps-sales",
  "www.hustleprofitmaps.au":  "profitmaps-sales",
  "demo.hustleprofitmaps.au": "profitmaps-sales",

  // ── HustleTap / TapNFC (iter374 M18, Feb 2026) — bright-orange
  //    marketing landing at ROOT for the NFC tap product line.
  //    All 4 apex domains + www/app/demo variants → same TapNfcSalesPage.
  //    EXCEPT the demo.* subdomains, which render the live icon-grid
  //    router at "/" so a visitor can experience a tap without
  //    holding a real tag.
  "hustletap.au":         "tapnfc-sales",
  "www.hustletap.au":     "tapnfc-sales",
  "app.hustletap.au":     "tapnfc-sales",
  "demo.hustletap.au":    "tapnfc-demo",
  "hustletap.com.au":     "tapnfc-sales",
  "www.hustletap.com.au": "tapnfc-sales",
  "app.hustletap.com.au": "tapnfc-sales",
  "demo.hustletap.com.au":"tapnfc-demo",
  "tapnfc.au":            "tapnfc-sales",
  "www.tapnfc.au":        "tapnfc-sales",
  "app.tapnfc.au":        "tapnfc-sales",  "demo.tapnfc.au":       "tapnfc-demo",
  "tapnfc.com.au":        "tapnfc-sales",
  "www.tapnfc.com.au":    "tapnfc-sales",
  "app.tapnfc.com.au":    "tapnfc-sales",
  "demo.tapnfc.com.au":   "tapnfc-demo",

  // ── Raro Full-Demo Vendor (iter375 R2, Feb 2026) — the "raro-site"
  //    mode swaps in RaroSiteRouter so /, /menu, /book, /takeaway,
  //    /table/N, /pos, /admin all serve the Raro pages at CLEAN paths
  //    (no /raro/ prefix).  app.* + demo.* + www.* all route the same
  //    way — patrons scanning a tabletop QR should never see a subfolder.
  "raro.au":              "raro-site",
  "www.raro.au":          "raro-site",
  "app.raro.au":          "raro-site",
  "demo.raro.au":         "raro-site",
  "raro.com.au":          "raro-site",
  "www.raro.com.au":      "raro-site",
  "app.raro.com.au":      "raro-site",
  "demo.raro.com.au":     "raro-site",
  "hustleraro.au":        "raro-site",
  "www.hustleraro.au":    "raro-site",
  "app.hustleraro.au":    "raro-site",
  "demo.hustleraro.au":   "raro-site",
  "hustleraro.com.au":    "raro-site",
  "www.hustleraro.com.au":"raro-site",
  "app.hustleraro.com.au":"raro-site",
  "demo.hustleraro.com.au":"raro-site",

  // ── HustleMeetups umbrella overview (iter337 — .com retired) ─
  // Bare hustlemeetups.au should render the OverviewPage in-place
  // at "/". www. alias included. hustlemeetups.com routes removed
  // after DNS cutover (user retired .com).

  // ── iter373c patch F (2026-09-22) — Whitelabel vendor sites.
  //    Each entry maps a whitelabel domain to `whitelabel:<full_slug>`
  //    so RootRouter can render <SiteRouter forceSlug={slug}/> inline
  //    with the browser URL staying at `/`. Sub-paths like
  //    `/pages/tax-savings` continue to hit the /r/:slug/* Route in
  //    App.js and work unchanged.
  "taxassistoxley.au":         "whitelabel:taxassist-accountants-oxley-4075",
  "www.taxassistoxley.au":     "whitelabel:taxassist-accountants-oxley-4075",
  "taxassistoxley.com.au":     "whitelabel:taxassist-accountants-oxley-4075",
  "www.taxassistoxley.com.au": "whitelabel:taxassist-accountants-oxley-4075",

  // ── VUMU New Farm (iter376 R3, 2026-02-27) — Australian-Italian-French
  //    bistro at 84b Merthyr Rd, New Farm QLD 4005. Uses the shared
  //    <RestaurantSite/> render (whitelabel:vumu → RestaurantSite forceSlug),
  //    so /, /menu, /about, /contact, /book, /cart all render inline at
  //    clean paths with the Trip Planner + Closest Facilities panels for
  //    free (they live in the RestaurantSite About section).
  "vumu.au":         "whitelabel:vumu",
  "www.vumu.au":     "whitelabel:vumu",
  "app.vumu.au":     "whitelabel:vumu",
  "demo.vumu.au":    "whitelabel:vumu",
};

/**
 * Extract the whitelabel slug from a hostname, or `null` if the host
 * is not a registered whitelabel domain. Consumers use this to force
 * the SiteRouter to render a specific vendor site at "/".
 */
export function resolveWhitelabel(hostname) {
  const raw = String(hostname || "").toLowerCase();
  const mode = resolveHostMode(raw);
  if (typeof mode === "string" && mode.startsWith("whitelabel:")) {
    return mode.slice("whitelabel:".length);
  }
  return null;
}

/**
 * Return `true` when the hostname is a Raro-branded standalone site.
 * The ChromeGate uses this to swap in the slim WhitelabelTopBar (no
 * platform Modules/Emergency/Account chrome, just language + clock
 * + light/dark toggle).
 */
export function resolveRaro(rawHost) {
  if (!rawHost) return false;
  return resolveHostMode(String(rawHost).toLowerCase()) === "raro-site";
}

/**
 * Hostname → business_id map for restaurant sites using the shared
 * restaurant template.  Every domain here MUST have a matching record
 * in one of {hustleeats.hp_raro_business, hustleeats.businesses,
 * hustlesuite_identity.businesses}.  New restaurants are onboarded by
 * (a) inserting the record with RESTAURANT_TOKEN_SCHEMA v1 defaults,
 * (b) adding one line here, (c) mapping the mode to "raro-site" above.
 */
const _RESTAURANT_HOST_TO_BUSINESS_ID = {
  "raro.au":                 "biz_raro_demo",
  "www.raro.au":             "biz_raro_demo",
  "app.raro.au":             "biz_raro_demo",
  "demo.raro.au":            "biz_raro_demo",
  "raro.com.au":             "biz_raro_demo",
  "www.raro.com.au":         "biz_raro_demo",
  "app.raro.com.au":         "biz_raro_demo",
  "demo.raro.com.au":        "biz_raro_demo",
  "hustleraro.au":           "biz_raro_demo",
  "www.hustleraro.au":       "biz_raro_demo",
  "app.hustleraro.au":       "biz_raro_demo",
  "demo.hustleraro.au":      "biz_raro_demo",
  "hustleraro.com.au":       "biz_raro_demo",
  "www.hustleraro.com.au":   "biz_raro_demo",
  "app.hustleraro.com.au":   "biz_raro_demo",
  "demo.hustleraro.com.au":  "biz_raro_demo",
  // iter376 (2026-09) — VUMU New Farm uses the same Raro POS/staff-login
  // components and hits the same generic /api/venue-pin/login and
  // /api/restaurant/{id}/brand endpoints.  Adding this row lets
  // RaroBrandContext / RaroStaffLoginPage resolve biz_vumu automatically
  // when the host is vumu.au.
  "vumu.au":                 "biz_vumu",
  "www.vumu.au":             "biz_vumu",
  "app.vumu.au":             "biz_vumu",
  "demo.vumu.au":            "biz_vumu",
};

/**
 * Resolve a restaurant business_id for the current host, or `null` if
 * the host is not a mapped restaurant domain.  Used by
 * RaroBrandContext to fetch the correct business record.
 */
export function resolveRestaurantBusinessId(rawHost) {
  if (!rawHost) return null;
  return _RESTAURANT_HOST_TO_BUSINESS_ID[String(rawHost).toLowerCase()] || null;
}

/**
 * Bare hostnames that should Navigate to a SPA path — used for
 * `app.` / `admin.` subdomains that jump straight into a dashboard.
 * These preserve the historical behaviour of RootRouter's
 * APP_HOST_ROUTES.
 */
export const APP_HOST_ROUTES = {
  "app.hustlesuite.au":       "/login",
  "app.hustleprofitmaps.au":  "/login?next=/admin/exposure-grid",
  "app.hustleplaza.au":       "/plaza/shop",
  // iter316m (2026-02) — plaza.au and www.plaza.au are dedicated
  // shopping-cart apex hosts. Unlike hustleplaza.au (which serves
  // the multilingual PlazaSalesPage marketing landing), plaza.au
  // MUST drop straight into the 200k-item catalogue so the domain
  // reads as "the shop" not "the pitch". This entry short-circuits
  // the plaza-mode fallback in RootRouter (resolveAppHostRoute runs
  // before getPlazaMode).
  "plaza.au":                 "/plaza/shop",
  "www.plaza.au":             "/plaza/shop",
  "admin.hustlesuite.au":     "/admin",
  "app.hustletravel.au":      "/roadtrip",
  "app.hustleevents.au":      "/hustle-events",
  "app.hustleconnect.au":     "/account/hustleconnect",
  "app.hustlemarketplace.au": "/hustleshop?marketplace=1",
  "app.hustlebargains.au":    "/deals",
  // app.hustleshield.au — Form Builder + Shield + Agency Console.
  // Anonymous visitors land on /login (gated by AppHostGate), signed-in
  // admins land on /admin/forms via postLoginPath().
  "app.hustleshield.au":      "/admin/forms",
};

/** Resolve a mode string for a hostname, or `null` when no direct match. */
export function resolveHostMode(rawHost) {
  if (!rawHost) return null;
  return _MAP[rawHost.toLowerCase()] || null;
}

/** Resolve a SPA path for a hostname when it should Navigate(), else null. */
export function resolveAppHostRoute(rawHost) {
  if (!rawHost) return null;
  return APP_HOST_ROUTES[rawHost.toLowerCase()] || null;
}

/** Modes rendered inline (not via <Navigate>) — used by RootRouter's dispatch. */
export const INLINE_MODES = new Set([
  "answer-app", "answer-demo", "answer-sales",
  "finder", "travel-sales", "dictate-sales", "events-sales",
  "meet-landing", "circles-sales", "matchmaker-sales",
  "roundup-sales", "rewards-sales", "directory-sales",
  "directory-app",
  "stadium-sales", "eats-sales", "connect-sales",
  "reviews-sales", "crm-sales", "press-sales",
  "profitmaps-sales",
  "suite-sales", "sales", "plaza", "plaza-demo",
  "pwa-launcher",
  "vendors-landing", "members-landing",
]);
